/**
 * RIG mode for unrigged models (SPEC "Unrigged Model Workflow" + auto-skin v2).
 *
 * Owns the unrigged pipeline: estimate a placement from the bounds, build the canonical skeleton
 * from the (user-corrected) placement, voxelize + walk the geodesics ONCE into a
 * `VertexDistanceCache`, solve the weights from that cache (milliseconds, so every parameter
 * change re-solves live), and swap the plain meshes for the `SkinnedMesh`es that
 * `assembleSkinnedRig` (`src/rig/assembleRig.ts`) builds. That construction is session-free on
 * purpose; this controller only owns the state around it.
 *
 * The ORIGINAL unskinned meshes and the distance cache both live here: regenerating must re-skin
 * the source geometry, never the already-skinned clone, and the cache (hundreds of ms) is what
 * makes the correction tools interactive. Leak checking / fixing, region reassignment, the masks
 * and the statistics live in `skinCorrection.ts`.
 */

import type { BufferGeometry, Group, Mesh, Skeleton, SkinnedMesh } from 'three';

import { AppError } from '../../shared';
import type {
  CanonicalBoneId,
  CanonicalRigPlacement,
  SkinData,
  SkinParams,
  SkinResolution,
} from '../../shared';
import { estimatePlacement as estimatePlacementFromBounds } from '../../rig/skeletonBuilder';
import type { BuiltCanonicalSkeleton } from '../../rig/skeletonBuilder';
import { assembleSkinnedRig } from '../../rig/assembleRig';
import { applySkinDataToGeometry, boneSegmentsFromPlacement } from '../../rig/skinning';
import type { BoneSegment } from '../../rig/skinning';
import { buildVertexDistanceCache, nearestBoneOf } from '../../rig/autoskin/distanceCache';
import { solveSkinWeightsDetailed } from '../../rig/autoskin/weightSolver';
import type { SolveStats } from '../../rig/autoskin/weightSolver';
import {
  DEFAULT_SKIN_PARAMS,
  checkSkinModelScale,
  isDefaultSkinParams,
} from '../../rig/autoskin/skinParams';
import { TEST_POSES } from '../../rig/autoskin/testPoses';
import type { TestPose, VertexDistanceCache } from '../../rig/autoskin/types';
import type { ImportedModel } from '../../importExport';
import type { ControllerDeps, InternalRigController } from './internal';
import { guard, guardSync, requireModel } from './internal';
import { boneIndexOf, createSkinCorrection, readMeshPositions } from './skinCorrection';

/**
 * Let the browser actually PAINT the busy state before a multi-hundred-ms voxelization blocks the
 * thread. One `setTimeout(0)` is not enough in a browser: the task can run before the frame is
 * committed, so the spinner never appears. Two frames plus a task guarantee the paint. Node (the
 * test environment) has no `requestAnimationFrame` and keeps the timeout path; the 100 ms escape
 * hatch covers a background tab, where rAF never fires at all.
 */
function yieldToUi(): Promise<void> {
  return new Promise((resolve) => {
    if (typeof requestAnimationFrame !== 'function') {
      setTimeout(resolve, 0);
      return;
    }
    let done = false;
    const finish = (): void => {
      if (done) return;
      done = true;
      resolve();
    };
    requestAnimationFrame(() => requestAnimationFrame(() => setTimeout(finish, 0)));
    setTimeout(finish, 100);
  });
}

export function createRigController(deps: ControllerDeps): InternalRigController {
  const { ctx } = deps;
  const { store, session, events } = ctx;

  /** The unskinned meshes every (re-)generation starts from. */
  let sourceMeshes: Mesh[] = [];
  /** The model as imported, before any skinned swap — the source of bounds/height and meshes. */
  let baseModel: ImportedModel | null = null;
  let built: BuiltCanonicalSkeleton | null = null;
  let skins: SkinData[] = [];
  let skinnedMeshes: SkinnedMesh[] = [];
  let rigRoot: Group | null = null;
  /** The exact placement object the current weights were computed for (staleness check). */
  let placementUsedForSkin: CanonicalRigPlacement | null = null;
  /** Restored weights are user work too (earlier-session corrections); regenerating discards them. */
  let restoredFromPersist = false;

  // --- GPU resource ownership -----------------------------------------------
  /**
   * What THIS controller allocated on the GPU side and is therefore responsible for releasing.
   *
   * Modelled on the companion `owned` map in `previewController.ts` (vault
   * `viewport-companions-onion`), and for the same reason: the live objects live somewhere else
   * (`session.model`, `skinnedMeshes`), and a path that empties that other place — a project
   * close, a new import — would otherwise take the only record of what needs freeing with it.
   *
   * Before 2026-08-22 nothing on the main model path called `dispose()` at all (audit §2.6 #1).
   * Every "Generate skin weights" runs `createSkinnedMesh`, which does `mesh.geometry.clone()`
   * per mesh (`src/rig/skeletonBuilder.ts`), and the previous clones were dropped by reference
   * only. Dropping the reference is not enough: three's `WebGLBindingStates` keeps
   * `bindingStates[geometry.id].attributes` alive and ONLY the geometry's own `dispose()` event
   * clears it, so the VAO and every uploaded attribute buffer stayed resident for the life of the
   * tab — measured in the audit at roughly 16 MB per Generate on a 200 k-vertex mesh.
   *
   * Scope is deliberately narrow — geometries and the built `Skeleton`'s bone texture, both
   * created by `assembleSkinnedRig` and referenced by nothing else:
   *  - MATERIALS are NOT ours. `createSkinnedMesh` passes `mesh.material` straight through, so
   *    the skinned mesh shares the material (and its textures) with the still-live source mesh in
   *    `sourceMeshes` and with the next re-skin. Disposing them here would blank the model the
   *    user is looking at.
   *  - The SOURCE geometries are not ours either: they come out of the importer with the model
   *    and must survive every re-skin (that is the whole point of `sourceMeshes`).
   */
  interface OwnedRigResources {
    geometries: BufferGeometry[];
    skeleton: Skeleton | null;
  }
  let owned: OwnedRigResources | null = null;

  /**
   * Release the previous assembly. Called only once the replacement is already in `session.model`
   * and the viewport has re-synced, so nothing on screen is pointing at what is freed here.
   */
  function disposeOwned(previous: OwnedRigResources | null): void {
    if (!previous) return;
    for (const geometry of previous.geometries) geometry.dispose();
    previous.skeleton?.dispose();
  }

  // --- the geodesic cache ---------------------------------------------------
  /** One per source mesh. Expensive; rebuilt only for a new placement or resolution. */
  let caches: (VertexDistanceCache | null)[] = [];
  let cachePlacement: CanonicalRigPlacement | null = null;
  let cacheResolution: SkinResolution | null = null;
  let cacheSegments: BoneSegment[] = [];
  let restPositions: (Float32Array | null)[] = [];
  /**
   * Bumped whenever the cache is invalidated. A build that started before the bump is stale by the
   * time it finishes and must not be written back (F5).
   */
  let cacheGeneration = 0;

  function captureSource(model: ImportedModel): Mesh[] {
    if (baseModel !== model && model.meshes.length > 0) {
      baseModel = model;
      sourceMeshes = [...model.meshes];
      restPositions = [];
    }
    return sourceMeshes;
  }

  function restOf(meshIndex: number): Float32Array | null {
    const mesh = sourceMeshes[meshIndex];
    if (!mesh) return null;
    const cached = restPositions[meshIndex];
    if (cached) return cached;
    const positions = readMeshPositions(mesh.geometry);
    restPositions[meshIndex] = positions;
    return positions;
  }

  function dropCaches(): void {
    caches = [];
    cachePlacement = null;
    cacheResolution = null;
    cacheSegments = [];
    // Anything already voxelizing is now building for a placement nobody wants (F5).
    cacheGeneration += 1;
    correction.recordSolve([], [], store.getState().rig.skinParams.resolution);
  }

  /**
   * Is there a cache the correction tools can use? Deliberately NOT a function of the selected
   * resolution: picking a new one must not re-voxelize behind the user's back (a 400 ms freeze on
   * a real character). The selection is an input to the next Generate; until then corrections keep
   * using the cache that exists (C2). `skinStats().resolution` reports the CACHE's resolution, so
   * a panel can compare it with `rig.skinParams.resolution` and offer "regenerate".
   */
  function hasCache(): boolean {
    const placement = store.getState().rig.placement;
    if (!placement || cachePlacement !== placement) return false;
    return (
      caches.length === sourceMeshes.length && caches.length > 0 && caches.every((c) => c !== null)
    );
  }

  /**
   * Do two parameter sets produce the same weights? Everything except `resolution`, which only
   * changes what the NEXT generation voxelizes (C2). Structural, because `setSkinParams` clones.
   */
  function sameSolveInputs(a: SkinParams, b: SkinParams): boolean {
    if (a === b) return true;
    if (a.blend !== b.blend || a.symmetry !== b.symmetry) return false;
    if (a.smoothingIterations !== b.smoothingIterations) return false;
    if (a.smoothingLambda !== b.smoothingLambda) return false;

    const keys = Object.keys(a.joints) as CanonicalBoneId[];
    if (keys.length !== Object.keys(b.joints).length) return false;
    for (const key of keys) {
      const ja = a.joints[key];
      const jb = b.joints[key];
      if (!ja || !jb || ja.offset !== jb.offset || ja.blend !== jb.blend) return false;
    }

    if (a.regionOverrides.length !== b.regionOverrides.length) return false;
    for (let i = 0; i < a.regionOverrides.length; i++) {
      const oa = a.regionOverrides[i];
      const ob = b.regionOverrides[i];
      if (oa.meshName !== ob.meshName || oa.boneId !== ob.boneId) return false;
      if (oa.vertices.length !== ob.vertices.length) return false;
      for (let v = 0; v < oa.vertices.length; v++) {
        if (oa.vertices[v] !== ob.vertices[v]) return false;
      }
    }
    return true;
  }

  /**
   * The model height the last scale warning was raised for, so re-generating at a different
   * resolution does not repeat the same toast. A different model has a different height and warns
   * again, which is the behaviour that matters.
   */
  let scaleWarningHeight: number | null = null;

  /**
   * Say out loud when the model is not metre-scale (audit §2.4 #3).
   *
   * Every length in `SkinParams` — the blend width, the per-joint offsets, the leak threshold —
   * is metres, but the geodesic distances they are compared against are in MODEL units. On a
   * centimetre-scale import the default 3 cm blend is 0.3 mm of model, so `falloff` in
   * `weightSolver.ts` flushes every neighbouring bone to zero: blending switches off entirely and
   * the leak check flags nearly every vertex. Both look like a bad model rather than a unit
   * mismatch, so nothing led the user to the real cause.
   *
   * Raised HERE, before the voxelization below, so it lands in front of the several-hundred-ms
   * wait instead of after it. Advisory exactly like `reportPreflight` in `projectController.ts`:
   * a scale we do not recognise is still the user's business, and refusing a skin they could have
   * completed is worse than letting them run it and see the warning.
   */
  function reportModelScale(height: number): void {
    const scale = checkSkinModelScale(height);
    if (!scale.warning) return;
    if (scaleWarningHeight === height) return;
    scaleWarningHeight = height;
    console.warn('[controllers] skin model scale', scale);
    store.actions.toast('info', scale.warning);
  }

  /** Voxelize + geodesics for every source mesh. Blocking; callers set `skinBusy` around it. */
  function buildCaches(placement: CanonicalRigPlacement, resolution: SkinResolution): void {
    const model = requireModel(ctx);
    const meshes = captureSource(model);
    if (meshes.length === 0) {
      throw new AppError('RIG_NO_MESH', 'This model has no mesh to skin.');
    }
    const height = baseModel?.height ?? model.height;
    const segments = boneSegmentsFromPlacement(placement, height);

    reportModelScale(height);

    caches = meshes.map((mesh, i) =>
      buildVertexDistanceCache(mesh.geometry, segments, {
        resolution,
        meshName: mesh.name || `mesh${i}`,
      }),
    );
    cachePlacement = placement;
    cacheResolution = resolution;
    cacheSegments = segments;
  }

  /** Solve every mesh from the cache; returns the weights and refreshes the statistics. */
  function solveAll(): SkinData[] {
    const params = store.getState().rig.skinParams;
    const weights: SkinData[] = [];
    const solved: VertexDistanceCache[] = [];
    const solveStats: SolveStats[] = [];

    for (let i = 0; i < caches.length; i++) {
      const cache = caches[i];
      if (!cache) continue;
      const positions = restOf(i);
      const result = solveSkinWeightsDetailed(
        cache,
        params,
        boneIndexOf,
        positions ? { positions, segments: cacheSegments } : undefined,
      );
      weights.push(result.skin);
      solved.push(cache);
      solveStats.push(result.stats);
    }

    // The CACHE's resolution: after a resolution change the weights still come from the old
    // voxelization until the next Generate (C2).
    correction.recordSolve(solved, solveStats, cacheResolution ?? params.resolution);
    return weights;
  }

  /**
   * Replace the displayed model with the skinned one. `meshes: []` matters: the plain meshes must
   * stop being rendered/exported, but they stay alive in `sourceMeshes` for the next re-skin.
   */
  function swapModel(model: ImportedModel, root: Group, meshes: SkinnedMesh[]): void {
    session.setModel({
      ...model,
      root,
      meshes: [],
      skinnedMeshes: meshes,
      skeleton: built ? built.skeleton : null,
      kind: 'unrigged',
    });
    session.setBuiltSkeleton(built ? built.skeleton : null);
    events.emit('model-changed');
  }

  function assemble(placement: CanonicalRigPlacement, weights: SkinData[], meshes: Mesh[]): void {
    // Construction lives in `src/rig/assembleRig.ts` so a read-only companion project can build
    // the same rig without an editor session; everything below is this controller's bookkeeping.
    const assembled = assembleSkinnedRig(meshes, placement, weights);

    // Held, not freed yet: the old geometries stay valid until `swapModel` has put the new root
    // into the session and the viewport's `'model-changed'` handler has re-pointed `modelLayer`
    // and `skinHeatLayer` at it. Freeing first would pull a geometry out from under a layer that
    // has not been told about the replacement.
    const previous = owned;
    owned = {
      geometries: assembled.skinnedMeshes.map((mesh) => mesh.geometry),
      skeleton: assembled.built.skeleton,
    };

    built = assembled.built;
    // `assembleSkinnedRig` pairs the entries to the meshes (by name, then positionally), so what
    // is stored here is the per-mesh order — which is what `allSkinData` saves.
    skins = assembled.skinData;
    skinnedMeshes = assembled.skinnedMeshes;
    rigRoot = assembled.root;
    placementUsedForSkin = placement;

    const model = requireModel(ctx);
    swapModel(model, assembled.root, assembled.skinnedMeshes);

    session.setRig(assembled.rig);
    // `rigBound` mirrors `session.rig` for the pure mode gate (EXPORT). `assemble` runs both on
    // skin generation and on the rebuild from persisted weights, so both paths set it.
    store.actions.setRigBound(true);

    // The swap above is complete and everything that renders is now on the new objects, so the
    // previous generation's GPU resources can go (audit §2.6 #1).
    disposeOwned(previous);
  }

  // --- live re-solve --------------------------------------------------------
  /** The parameter object the live geometry currently reflects (identity comparison). */
  let solvedParams = store.getState().rig.skinParams;
  let resolving = false;
  let resolveDirty = false;
  /** A parameter change that arrived while the voxelizer was running (F4). */
  let paramsChangedWhileBusy = false;
  /** The voxelization currently running, shared by every caller that needs a cache (C3). */
  let inFlightCacheBuild: Promise<void> | null = null;

  /**
   * `rig.skinBusy` as a COUNTER (C3). Busy phases nest — the eager cache build after a reload can
   * still be running when the user hits Generate — and a plain boolean let the inner phase clear
   * the flag while the outer one was still blocking the thread.
   */
  let busyDepth = 0;

  function beginBusy(): void {
    busyDepth += 1;
    if (busyDepth === 1) store.actions.setSkinBusy(true);
  }

  function endBusy(): void {
    busyDepth = Math.max(0, busyDepth - 1);
    if (busyDepth > 0) return;
    store.actions.setSkinBusy(false);
    flushParamsChangedWhileBusy();
  }

  /** End of the LAST busy phase: pick up whatever the user changed while the thread blocked (F4). */
  function flushParamsChangedWhileBusy(): void {
    if (!paramsChangedWhileBusy) return;
    paramsChangedWhileBusy = false;
    queueMicrotask(() => {
      const state = store.getState();
      if (!state.rig.skinGenerated || state.rig.skinBusy) return;
      if (sameSolveInputs(state.rig.skinParams, solvedParams) && hasCache()) return;
      void guardedResolve();
    });
  }

  /**
   * Build the cache for the CURRENT placement. The yield below is a real gap: the user can move a
   * joint (which drops the caches) while we wait, so the token and the placement are re-checked
   * afterwards and a stale build is abandoned rather than written (F5).
   */
  async function buildCacheNow(): Promise<void> {
    const placement = store.getState().rig.placement;
    if (!placement) {
      throw new AppError('RIG_NO_PLACEMENT', 'Place the skeleton before generating skin weights.');
    }
    const resolution = store.getState().rig.skinParams.resolution;
    const token = cacheGeneration;
    beginBusy();
    try {
      // Yield first: the panel must be able to paint "working…" before the thread blocks.
      await yieldToUi();
      if (cacheGeneration !== token) return;
      if (store.getState().rig.placement !== placement) return;
      buildCaches(placement, resolution);
    } finally {
      endBusy();
    }
  }

  /**
   * One voxelization at a time: the promise is shared, so the eager build after a reload and a
   * Generate the user starts a moment later wait on the same work instead of doing it twice (C3).
   */
  function ensureCache(): Promise<void> {
    if (hasCache()) return Promise.resolve();
    if (inFlightCacheBuild) return inFlightCacheBuild;
    const build = buildCacheNow();
    inFlightCacheBuild = build;
    return build.finally(() => {
      if (inFlightCacheBuild === build) inFlightCacheBuild = null;
    });
  }

  async function resolveOnce(): Promise<void> {
    if (!store.getState().rig.skinGenerated || skinnedMeshes.length === 0) return;
    await ensureCache();
    // `ensureCache` can legitimately abandon its build (the placement moved under it); solving
    // against no cache would throw, and the placement subscription is already handling it.
    if (!hasCache()) return;
    const params = store.getState().rig.skinParams;
    const weights = solveAll();
    for (let i = 0; i < skinnedMeshes.length && i < weights.length; i++) {
      skins[i] = weights[i];
      applySkinDataToGeometry(skinnedMeshes[i].geometry, weights[i]);
    }
    solvedParams = params;
    events.emit('skin-changed');
    // The standing leak report describes the PREVIOUS weights. Only flag it here: running the
    // check on every throttled frame of a slider drag allocated a mask and dispatched a fresh
    // `LeakSummary` per frame (C1). The commit paths call `refreshAfterSolve`.
    correction.markLeakStale();
    deps.scheduleSave();
  }

  /**
   * Coalesced re-solve. A burst of slider changes must produce ONE solve: calls that arrive while
   * a solve is running only mark the result dirty, and the trailing run is skipped when the
   * parameters have not moved since the solve that is already finishing.
   */
  async function resolveFromCache(): Promise<void> {
    if (resolving) {
      resolveDirty = true;
      return;
    }
    resolving = true;
    try {
      await resolveOnce();
      while (resolveDirty) {
        resolveDirty = false;
        if (store.getState().rig.skinParams === solvedParams) break;
        await resolveOnce();
      }
    } finally {
      resolving = false;
    }
  }

  /**
   * "Would regenerating throw work away?" — the question the RIG panel's confirm asks (F7).
   *
   * Only meaningful once weights exist: before the first generation there is nothing to lose.
   * The RESOLUTION is deliberately excluded — it is an input to the next generation, not a
   * correction of the current one, so picking a resolution before the first Generate must not
   * raise a "you will lose your edits" prompt.
   */
  function hasSkinEdits(): boolean {
    if (!store.getState().rig.skinGenerated) return false;
    if (restoredFromPersist) return true;
    const params = store.getState().rig.skinParams;
    return !isDefaultSkinParams({ ...params, resolution: DEFAULT_SKIN_PARAMS.resolution });
  }

  /**
   * The committing form: a panel calling `resolveFromCache()` (or a correction tool) is a user
   * action that must survive a refresh one keystroke later, so it forces the write instead of
   * leaving it on the 300 ms debounce. Subscription-driven re-solves (a slider being dragged)
   * keep the debounce — see `resolveOnce` (F3).
   */
  async function commitResolve(): Promise<void> {
    await resolveFromCache();
    await deps.saveNow();
  }

  /**
   * What a store subscription may call: never let a rejected re-solve become an unhandled
   * rejection — `guard` toasts it and re-throws, so the rejection is swallowed here (C6).
   */
  function guardedResolve(): Promise<void> {
    return guard(ctx, 'The skin weights could not be re-solved.', resolveFromCache).catch(
      () => undefined,
    );
  }

  const correction = createSkinCorrection({
    ctx,
    caches: () => caches,
    skins: () => skins,
    restPositions: restOf,
    segments: () => cacheSegments,
    builtSkeleton: () => built,
    ensureCache,
    resolveFromCache: commitResolve,
    beginBusy,
    endBusy,
    yieldToUi,
  });

  /**
   * Moving a joint invalidates the weights that were computed for the old placement. Deferred
   * with `queueMicrotask` — dispatching an action synchronously from inside a store subscription
   * is not allowed.
   */
  const unsubscribePlacement = store.select(
    (state) => state.rig.placement,
    (placement) => {
      if (placement === null || placement === placementUsedForSkin) return;
      if (!store.getState().rig.skinGenerated) return;
      queueMicrotask(() => {
        const state = store.getState();
        if (!state.rig.skinGenerated || state.rig.placement === placementUsedForSkin) return;
        dropCaches();
        store.actions.setSkinGenerated(false);
        store.actions.toast('info', 'Joints moved — regenerate the skin weights.');
      });
    },
  );

  /**
   * Every parameter change re-solves from the cache: that is the whole point of auto-skin v2.
   * Except a resolution change, which describes the next VOXELIZATION and must not re-solve or
   * rebuild anything on its own (C2).
   */
  const unsubscribeParams = store.select(
    (state) => state.rig.skinParams,
    () => {
      queueMicrotask(() => {
        const state = store.getState();
        if (!state.rig.skinGenerated) return;
        if (state.rig.skinBusy) {
          // The voxelizer owns the thread; remember to catch up when it lets go (F4).
          paramsChangedWhileBusy = true;
          return;
        }
        if (sameSolveInputs(state.rig.skinParams, solvedParams)) return;
        void guardedResolve();
      });
    },
  );

  const controller: InternalRigController = {
    estimatePlacement(): void {
      guardSync(ctx, 'The skeleton placement could not be estimated.', () => {
        const model = requireModel(ctx);
        captureSource(model);
        store.actions.setPlacement(estimatePlacementFromBounds(model.bounds));
      });
    },

    async generateSkin(): Promise<void> {
      return guard(ctx, 'Skin weights could not be generated.', async () => {
        // The yield below is a real gap: a joint drag landing in it would leave the weights bound
        // to a placement the user has already moved away from. So the placement is re-read after
        // the yield and the whole generation restarts on the new one (F5).
        let attempts = 0;
        // ONE busy phase for the whole generation, and it stays busy across an eager cache build
        // it may be waiting on — nested phases are counted, never overwritten (C3).
        beginBusy();
        try {
          for (;;) {
            const placement = store.getState().rig.placement;
            if (!placement) {
              throw new AppError(
                'RIG_NO_PLACEMENT',
                'Place the skeleton before generating skin weights.',
              );
            }
            // `buildCaches` re-checks the model and the meshes; this is only for `assemble`.
            const meshes = captureSource(requireModel(ctx));
            const params = store.getState().rig.skinParams;
            // Before the solve: `reset` clears the statistics too, and the toast below reads them.
            correction.reset();

            // A reload starts an eager voxelization; hitting Generate a moment later must WAIT for
            // it rather than voxelize the same placement a second time (C3).
            if (inFlightCacheBuild) await inFlightCacheBuild.catch(() => undefined);
            await yieldToUi();

            const stale = store.getState().rig.placement !== placement;
            if (!stale) {
              const reusable =
                hasCache() && cachePlacement === placement && cacheResolution === params.resolution;
              if (!reusable) {
                dropCaches();
                buildCaches(placement, params.resolution);
              }
              assemble(placement, solveAll(), meshes);
              break;
            }

            attempts += 1;
            if (attempts >= 3) {
              throw new AppError(
                'RIG_PLACEMENT_MOVED',
                'The skeleton kept moving while the weights were being generated. Try again.',
              );
            }
          }
        } finally {
          endBusy();
        }

        restoredFromPersist = false;
        solvedParams = store.getState().rig.skinParams;
        // `solveAll` already used the newest parameters, so this is usually a no-op; it matters
        // when the RESOLUTION changed during the build, which the fresh cache does not reflect.
        flushParamsChangedWhileBusy();

        store.batch(() => {
          store.actions.setSkinGenerated(true);
          store.actions.setLeakSummary(null);
        });
        events.emit('skin-changed');
        events.emit('rig-changed');

        const stats = correction.skinStats();
        if (stats) {
          const total = Math.round(stats.buildMs + stats.geodesicMs + stats.solveMs);
          store.actions.toast(
            'info',
            `Skin generated in ${total} ms · res ${stats.resolution} · ` +
              `${stats.closed ? 'closed' : 'open'} shell · ${stats.unreachable} unreachable`,
          );
        }

        await deps.controllers.project.saveProjectState();
      });
    },

    ensureCache(): Promise<void> {
      return guard(ctx, 'The geodesic distance cache could not be built.', ensureCache);
    },

    hasCache,

    resolveFromCache(): Promise<void> {
      // Public = a committed edit (slider release, panel button): re-solve, force the write (F3),
      // and bring the leak report back in line with the weights (C1).
      return guard(ctx, 'The skin weights could not be re-solved.', async () => {
        await commitResolve();
        await correction.refreshAfterSolve();
      });
    },

    runLeakCheck: correction.runLeakCheck,
    fixLeaks: correction.fixLeaks,
    revertLastFix: correction.revertLastFix,
    reassignRegionAt: correction.reassignRegionAt,
    clearRegionOverrides: correction.clearRegionOverrides,
    leakMask: correction.leakMask,

    influenceMask: correction.influenceMask,
    skinStats: correction.skinStats,

    testPoses: (): readonly TestPose[] => TEST_POSES,
    hasSkinEdits,
    saveNow: (): void => void deps.saveNow(),
    skinnedMesh: (meshIndex = 0): SkinnedMesh | null => skinnedMeshes[meshIndex] ?? null,
    skinData: (meshIndex = 0): SkinData | null => skins[meshIndex] ?? null,
    allSkinData: (): SkinData[] => [...skins],
    builtSkeleton: (): BuiltCanonicalSkeleton | null => built,
    riggedRoot: (): Group | null => rigRoot,

    nearestBoneOfVertex(meshIndex: number, vertexIndex: number): CanonicalBoneId | null {
      const cache = caches[meshIndex];
      if (!cache || vertexIndex < 0 || vertexIndex >= cache.vertexCount) return null;
      const bone = nearestBoneOf(cache, cache.uniqueOf[vertexIndex]);
      return bone < 0 ? null : (cache.boneIds[bone] ?? null);
    },

    async confirmRig(): Promise<void> {
      return guard(ctx, 'The rig could not be confirmed.', async () => {
        if (!store.getState().rig.skinGenerated || rigRoot === null) {
          await controller.generateSkin();
        } else {
          await deps.controllers.project.saveProjectState();
        }
        store.actions.toast('info', 'Rig confirmed — the model is ready to animate.');
      });
    },

    rebuildFromPersisted(placement: CanonicalRigPlacement, skinData: readonly SkinData[]): void {
      guardSync(ctx, 'The stored skin weights could not be restored.', () => {
        const model = requireModel(ctx);
        const meshes = captureSource(model);
        if (meshes.length === 0) {
          throw new AppError('RIG_NO_MESH', 'This model has no mesh to skin.');
        }

        // No distance cache exists yet — the first correction (or parameter change) builds it
        // lazily through `ensureCache` (amendment A4).
        dropCaches();
        correction.reset();
        // Pairing stored entries to meshes (by `meshName`, positional fallback) lives in
        // `assembleSkinnedRig`, so the restore path and a companion loader match identically.
        assemble(placement, [...skinData], meshes);
        restoredFromPersist = true;
        solvedParams = store.getState().rig.skinParams;
        store.actions.setSkinGenerated(true);
        events.emit('skin-changed');
        events.emit('rig-changed');

        // Build the cache NOW rather than on the first slider commit (F2). Doing it lazily meant
        // the user's first drag stalled and flipped `skinBusy` mid-gesture, which rebuilt the
        // panel under their cursor. At load time a busy flash is free. Fire-and-forget: a failure
        // is reported by `ensureCache`'s own guard and the lazy path still covers it.
        void controller.ensureCache().catch(() => undefined);
      });
    },

    resetInternal(): void {
      /*
       * The one place a MODEL SWAP is released. `projectController` calls this from both
       * `loadIntoSession` (a new import replaces the model) and `closeProject`, so the rig this
       * controller assembled for the outgoing project is freed on both transitions rather than
       * being dropped by reference and kept alive by `WebGLBindingStates` (audit §2.6 #1).
       *
       * Ordering is safe because both callers are synchronous: nothing renders between this call
       * and the `'model-changed'` emit that follows it, so the viewport never draws a geometry
       * after it was disposed.
       */
      disposeOwned(owned);
      owned = null;

      sourceMeshes = [];
      baseModel = null;
      built = null;
      skins = [];
      skinnedMeshes = [];
      rigRoot = null;
      placementUsedForSkin = null;
      restoredFromPersist = false;
      restPositions = [];
      dropCaches();
      correction.reset();
      solvedParams = store.getState().rig.skinParams;
      /*
       * The memo is per MODEL, so it has to die with the model. It is keyed by height, and two
       * projects using the same file have the same height: import a centimetre-scale model, open
       * a second project on it, hit Generate — and `reportModelScale` found its own value still
       * sitting there and said nothing. A unit mismatch that produces no blending and a leak
       * report over nearly every vertex, with no warning and no console line, is precisely the
       * failure this warning exists to name.
       */
      scaleWarningHeight = null;
    },

    dispose(): void {
      unsubscribePlacement();
      unsubscribeParams();
      // Tearing the editor down (the DEV harness does exactly this between scenarios) must not
      // leave the last generated rig resident.
      disposeOwned(owned);
      owned = null;
    },
  };

  return controller;
}
